/* eslint-disable no-param-reassign */
import type { App, DirectiveBinding } from 'vue';
import mitt from 'mitt';
import Tooltip from 'bootstrap/js/dist/tooltip';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import i18n from '@/i18n';
import * as filters from '@/shared/helpers/filters';
import { capitalize } from '@/shared/helpers/capitalize';
import { encodeHtml } from '@/shared/helpers/encode';
import { decodeHtml } from '@/shared/helpers/decode';
import { staticSelectOptions } from '@/shared/helpers/static-select-options';
import { animateElement } from '@/shared/helpers/animate-element';
import { cloneObject } from '@/shared/helpers/clone-object';
import { formatUrl } from '@/shared/helpers/format-url';
import { snakeToCamel } from '@/shared/helpers/snake-to-camel';
import { snakeToCapitalized } from '@/shared/helpers/snake-to-capitalized';
import { subsetObject } from '@/shared/helpers/subset-object';
import { secondsToTime } from '@/shared/helpers/seconds-to-time';
import { isObjectDifferent } from '@/shared/helpers/is-object-different';
import { formatNumberToPhone } from '@/shared/helpers/format-number-to-phone';
import { formatToUrl } from '@/shared/helpers/format-to-url';
import { removeHtml } from '@/shared/helpers/remove-html';
import globalMixin from '@/mixins/global-mixin';
import { createRsGlobals } from '@/mixins/root-mixin';
import {
  formatAutoResponderDate,
  formatDate,
  formatTime,
  formatDateTimeCustom,
} from '@/shared/helpers/date-helpers';

const tooltipInstances = new WeakMap<HTMLElement, Tooltip>();

export const globalUtils = {
  ...filters,
  capitalize,
  decodeHtml,
  encodeHtml,
  staticSelectOptions,
  animateElement,
  cloneObject,
  formatUrl,
  snakeToCamel,
  snakeToCapitalized,
  isObjectDifferent,
  subsetObject,
  secondsToTime,
  formatNumberToPhone,
  formatToUrl,
  removeHtml,
  formatDate,
  formatTime,
  formatAutoResponderDate,
  formatDateTimeCustom,
};

export type TUtilsMethods = typeof globalUtils;
export type TFiltersMethods = typeof filters;

export const RsUtilitiesPlugin = {
  install(app: App) {
    // Align dayjs locale with the resolved i18n locale so all date formatting
    // (filters, $utils.formatDateTimeCustom, etc.) respects the active language
    // without requiring per-call locale handling or new translation keys.
    dayjs.locale(i18n.locale);

    app.config.globalProperties.$utils = globalUtils;
    app.config.globalProperties.$env = window.APP_CONFIG || { VUE_APP_ENV: 'development' };
    app.config.globalProperties.$eventBus = mitt();
    app.config.globalProperties.$filters = filters;

    // Typed wrapper around $t(): returns string instead of TranslateResult,
    // removing the need for `as string` casts or String() wrappers in templates.
    app.config.globalProperties.$ts = function $ts(key: string, ...args: any[]): string {
      return (this.$t as (...a: any[]) => string)(key, ...args);
    };

    app.config.globalProperties.$rsGlobals = createRsGlobals(app);

    app.mixin(globalMixin as any);

    // Vue 2 filter registrations — kept for reference during Track B migration
    // vue.filter('rsDateTime', filters.rsDateTime);
    // vue.filter('rsFullDateTime', filters.rsFullDateTime);
    // vue.filter('rsDate', filters.rsDate);
    // vue.filter('rsFancyDate', filters.rsFancyDate);
    // vue.filter('rsFancyKey', filters.rsFancyKey);
    // vue.filter('rsISODateTime', filters.rsISODateTime);
    // vue.filter('rsUnixSecondsToDateTime', filters.rsUnixSecondsToDateTime);
    // vue.filter('rsFilename', filters.rsFilename);
    // vue.filter('rsFancyFilename', filters.rsFancyFilename);
    // vue.filter('rsDecodeHtml', filters.rsDecodeHtml);
    // vue.filter('capitalize', filters.capitalize);

    app.directive('drag-value', (el: HTMLElement & { dragValue?: unknown }, binding: DirectiveBinding<unknown>) => {
      /* eslint-disable-next-line */
      el.dragValue = binding.value;
    });

    app.directive('click-outside', {
      mounted(el: HTMLElement & { clickOutsideEvent?: (e: MouseEvent) => void }, binding: DirectiveBinding<(e: MouseEvent) => void>) {
        /* eslint-disable-next-line */
        el.clickOutsideEvent = (event: MouseEvent) => {
          if (!(el === event.target || el.contains(event.target as Node))) {
            // Vue 3 removed vnode.context / binding.expression; binding.value is the bound handler.
            binding.value(event);
          }
        };
        document.body.addEventListener('click', el.clickOutsideEvent);
      },

      unmounted(el: HTMLElement & { clickOutsideEvent?: (e: MouseEvent) => void }) {
        if (el.clickOutsideEvent) {
          document.body.removeEventListener('click', el.clickOutsideEvent);
        }
      },
    });
    app.directive('drag-class', (el: HTMLElement & { dragClass?: unknown }, binding: DirectiveBinding<unknown>) => {
      /* eslint-disable-next-line */
      el.dragClass = binding.value;
    });
    app.directive('permalink', (el) => {
      const { value } = el.tagName === 'INPUT' ? (el as HTMLInputElement) : el.getElementsByTagName('input')[0];

      // eslint-disable-next-line no-param-reassign
      (el.tagName === 'INPUT' ? (el as HTMLInputElement) : el.getElementsByTagName('input')[0]).value = formatToUrl(value || '');
    });
    app.directive('permalink-with-underscore', (el) => {
      const { value } = el.tagName === 'INPUT' ? (el as HTMLInputElement) : el.getElementsByTagName('input')[0];

      // eslint-disable-next-line no-param-reassign
      (el.tagName === 'INPUT' ? (el as HTMLInputElement) : el.getElementsByTagName('input')[0]).value = formatToUrl(value || '', true);
    });

    app.directive('rs-tooltip', {
      beforeMount(el: HTMLElement, binding: DirectiveBinding<string | { title: string; disabled?: boolean }>) {
        const placement = Object.keys(binding.modifiers).find(
          (m) => ['top', 'bottom', 'left', 'right'].includes(m),
        ) ?? 'top';
        const title: string = typeof binding.value === 'object' ? binding.value.title : binding.value;
        const disabled: boolean = typeof binding.value === 'object' ? !!binding.value.disabled : false;
        if (!title || disabled) return;
        tooltipInstances.set(el, new Tooltip(el, {
          title, placement: placement as 'top' | 'bottom' | 'left' | 'right', trigger: 'hover focus', container: document.body, boundary: 'window', popperConfig: { positionFixed: true },
        }));
      },
      updated(el: HTMLElement, binding: DirectiveBinding<string | { title: string; disabled?: boolean }>) {
        const instance = tooltipInstances.get(el);
        const title: string = typeof binding.value === 'object' ? binding.value.title : binding.value;
        const disabled: boolean = typeof binding.value === 'object' ? !!binding.value.disabled : false;
        if (instance) {
          const tooltipId = el.getAttribute('aria-describedby');
          if (tooltipId) {
            document.getElementById(tooltipId)?.remove();
          }
          instance.dispose();
          tooltipInstances.delete(el);
        }
        if (title && !disabled) {
          const placement = Object.keys(binding.modifiers).find(
            (m) => ['top', 'bottom', 'left', 'right'].includes(m),
          ) ?? 'top';
          tooltipInstances.set(el, new Tooltip(el, {
            title, placement: placement as 'top' | 'bottom' | 'left' | 'right', trigger: 'hover focus', container: document.body, boundary: 'window', popperConfig: { positionFixed: true },
          }));
        }
      },
      unmounted(el: HTMLElement) {
        // Bootstrap's dispose() removes the tip via jQuery which can fail mid-animation; use aria-describedby to remove it directly from the DOM.
        const tooltipId = el.getAttribute('aria-describedby');
        if (tooltipId) {
          document.getElementById(tooltipId)?.remove();
        }
        tooltipInstances.get(el)?.dispose();
        tooltipInstances.delete(el);
      },
    });
  },
};
